/**
 * DataBest — Assignments page (Supervisor+).
 *
 * Manage caregiver ↔ individual assignments with rotation history.
 * Supports: create, end (close), delete (if no measurements in window),
 * filter by house/caregiver/individual/status/role, search.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Plus,
    Search,
    Loader2,
    Trash2,
    Circle,
    RefreshCw,
    Users2,
    XCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { assignmentsApi } from "../api/assignmentsApi";
import { housesApi } from "@/lib/orgApi";
import { individualsApi } from "@/lib/planApi";
import AssignmentFormDrawer from "../components/AssignmentFormDrawer";
import AssignmentRoleBadge from "../components/AssignmentRoleBadge";
import EndAssignmentPopover from "../components/EndAssignmentPopover";
import { formatDateUS, todayISO } from "@/lib/dates";

const STATUS_PILLS = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "ended", label: "Ended" },
];

const ROLE_PILLS = [
    { value: "", label: "All roles" },
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
    { value: "backup", label: "Backup" },
];

const AssignmentsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("active");
    const [role, setRole] = useState("");
    const [houseId, setHouseId] = useState("");
    const [individualId, setIndividualId] = useState("");
    const [houses, setHouses] = useState([]);
    const [individuals, setIndividuals] = useState([]);
    const [query, setQuery] = useState("");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [cancelingId, setCancelingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        housesApi.list().then(setHouses).catch(() => setHouses([]));
        individualsApi
            .list({ active: true })
            .then((r) => setIndividuals(r || []))
            .catch(() => setIndividuals([]));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status, page: 1, page_size: 100 };
            if (role) params.role = role;
            if (houseId) params.house_id = houseId;
            if (individualId) params.individual_id = individualId;
            const data = await assignmentsApi.list(params);
            setItems(data.items || []);
        } catch (err) {
            toast.error(err?.message || "Could not load assignments");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [status, role, houseId, individualId]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (a) =>
                a.caregiver_name.toLowerCase().includes(q) ||
                a.individual_name.toLowerCase().includes(q) ||
                (a.house_name || "").toLowerCase().includes(q),
        );
    }, [items, query]);

    const activeByIndividual = useMemo(() => {
        // For visual sanity — count of active assignments per individual (across list)
        const c = {};
        for (const a of items) {
            if (a.is_active) {
                c[a.individual_id] = (c[a.individual_id] || 0) + 1;
            }
        }
        return c;
    }, [items]);

    const handleCancel = async (row) => {
        if (
            !window.confirm(
                `Cancel assignment "${row.caregiver_name} → ${row.individual_name}" before it starts? This will permanently delete it.`,
            )
        )
            return;
        setCancelingId(row.id);
        try {
            await assignmentsApi.remove(row.id);
            toast.success("Assignment canceled");
            await load();
        } catch (err) {
            toast.error(err?.message || "Could not cancel assignment");
        } finally {
            setCancelingId(null);
        }
    };

    const handleDelete = async (row) => {
        if (
            !window.confirm(
                "Delete this assignment permanently? This is only allowed if no measurements were recorded in its window.",
            )
        )
            return;
        setDeletingId(row.id);
        try {
            await assignmentsApi.remove(row.id);
            toast.success("Assignment deleted");
            await load();
        } catch (err) {
            toast.error(err?.message || "Could not delete");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="mx-auto max-w-6xl" data-testid="assignments-page">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
            >
                {/* Header */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue-700">
                            Plans & Reports
                        </p>
                        <h1 className="mt-1 font-heading text-3xl font-bold text-brand-blue-950">
                            Caregiver Assignments
                        </h1>
                        <p className="mt-1 text-sm text-slate-500">
                            Track who is responsible for each individual over time.
                            Supports rotation and multiple roles per individual.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={load}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                            data-testid="assignments-refresh"
                        >
                            <RefreshCw
                                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                            />
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={() => setDrawerOpen(true)}
                            data-testid="assignments-new-btn"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue-700 px-3 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-brand-blue-800"
                        >
                            <Plus className="h-4 w-4" />
                            New assignment
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="mt-6 space-y-3 rounded-2xl border border-slate-200 bg-white/85 p-4 backdrop-blur">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                            <input
                                type="search"
                                placeholder="Search caregiver / individual / house…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="assignments-search"
                                className="h-9 w-72 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30"
                            />
                        </div>

                        <div className="flex flex-wrap gap-1">
                            {STATUS_PILLS.map((s) => (
                                <button
                                    key={s.value}
                                    type="button"
                                    onClick={() => setStatus(s.value)}
                                    data-testid={`assignments-filter-status-${s.value}`}
                                    data-active={status === s.value || undefined}
                                    className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                        status === s.value
                                            ? "bg-brand-blue-700 text-white"
                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>

                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            data-testid="assignments-filter-role"
                            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700"
                        >
                            {ROLE_PILLS.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>

                        {houses.length > 1 && (
                            <select
                                value={houseId}
                                onChange={(e) => setHouseId(e.target.value)}
                                data-testid="assignments-filter-house"
                                className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700"
                            >
                                <option value="">All houses</option>
                                {houses.map((h) => (
                                    <option key={h.id} value={h.id}>
                                        {h.name}
                                    </option>
                                ))}
                            </select>
                        )}

                        {individuals.length > 1 && (
                            <select
                                value={individualId}
                                onChange={(e) => setIndividualId(e.target.value)}
                                data-testid="assignments-filter-individual"
                                className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700"
                            >
                                <option value="">All individuals</option>
                                {individuals.map((i) => (
                                    <option key={i.id} value={i.id}>
                                        {i.first_name} {i.last_name}
                                    </option>
                                ))}
                            </select>
                        )}

                        <span
                            className="ml-auto rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-600"
                            data-testid="assignments-total"
                        >
                            {filtered.length} shown
                        </span>
                    </div>
                </div>

                {/* Table */}
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    {loading ? (
                        <div className="flex items-center justify-center p-12 text-slate-500">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading assignments…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-12 text-center" data-testid="assignments-empty">
                            <Users2 className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                            <p className="text-sm font-semibold text-slate-500">
                                No assignments match these filters
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                                Click <b>New assignment</b> to create one.
                            </p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-slate-100 text-sm">
                            <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Caregiver</th>
                                    <th className="px-3 py-2 text-left">Individual</th>
                                    <th className="px-3 py-2 text-left">House</th>
                                    <th className="px-3 py-2 text-left">Role</th>
                                    <th className="px-3 py-2 text-left">From → To</th>
                                    <th className="px-3 py-2 text-left">Duration</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filtered.map((a) => (
                                    <tr
                                        key={a.id}
                                        data-testid={`assignments-row-${a.id}`}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="px-3 py-2 font-semibold text-slate-900">
                                            {a.caregiver_name}
                                            <div className="text-[10px] font-normal text-slate-400">
                                                {a.caregiver_email}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 font-medium text-slate-800">
                                            {a.individual_name}
                                            {activeByIndividual[a.individual_id] > 1 && (
                                                <span className="ml-1 rounded-full bg-brand-blue-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-brand-blue-800">
                                                    {activeByIndividual[a.individual_id]} active
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-slate-600">
                                            {a.house_name}
                                        </td>
                                        <td className="px-3 py-2">
                                            <AssignmentRoleBadge role={a.role} />
                                        </td>
                                        <td className="px-3 py-2 text-slate-700">
                                            <span className="font-mono text-[11px]">
                                                {formatDateUS(a.effective_from)}
                                            </span>
                                            <span className="mx-1 text-slate-400">→</span>
                                            <span className="font-mono text-[11px]">
                                                {a.effective_to
                                                    ? formatDateUS(a.effective_to)
                                                    : "—"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 tabular-nums text-slate-600">
                                            {a.duration_days}d
                                        </td>
                                        <td className="px-3 py-2">
                                            {a.is_active ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-brand-green-100 px-2 py-0.5 text-[10px] font-bold uppercase text-brand-green-800">
                                                    <Circle className="h-2 w-2 fill-current" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
                                                    Ended
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                {a.is_active &&
                                                    (a.effective_from > todayISO() ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCancel(a)}
                                                            disabled={cancelingId === a.id}
                                                            data-testid={`assignments-cancel-btn-${a.id}`}
                                                            className="inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-[10px] font-bold text-orange-800 hover:bg-orange-100 disabled:opacity-60"
                                                            title="Cancel this future assignment (no history impact)"
                                                        >
                                                            {cancelingId === a.id ? (
                                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <XCircle className="h-3 w-3" />
                                                            )}
                                                            Cancel
                                                        </button>
                                                    ) : (
                                                        <EndAssignmentPopover
                                                            assignment={a}
                                                            onEnded={load}
                                                        />
                                                    ))}
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(a)}
                                                    disabled={deletingId === a.id}
                                                    data-testid={`assignments-delete-btn-${a.id}`}
                                                    className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                                                    title="Delete (only if no measurements exist)"
                                                >
                                                    {deletingId === a.id ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </motion.div>

            <AssignmentFormDrawer
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                onSaved={() => load()}
            />
        </div>
    );
};

export default AssignmentsPage;
